import React, { useState, useEffect } from 'react';
import { History, FileText, CheckCircle, Clock, XCircle, Save, ChevronDown, ChevronUp, User, RefreshCw, Trash2, AlertTriangle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const STATUS_CONFIG = {
  processing: { label: 'Processing', color: 'bg-yellow-100 text-yellow-700', Icon: Clock },
  reviewed:   { label: 'Reviewed',   color: 'bg-blue-100 text-blue-700',   Icon: CheckCircle },
  saved:      { label: 'Saved',      color: 'bg-green-100 text-green-700', Icon: Save },
  cancelled:  { label: 'Cancelled',  color: 'bg-red-100 text-red-700',     Icon: XCircle },
};

function formatSize(bytes) {
  if (!bytes) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

export default function ProcessHistory() {
  const { authFetch, isAdmin } = useAuth();
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [confirmReset, setConfirmReset] = useState(false);
  const [resetting, setResetting] = useState(false);

  const fetchHistory = async () => {
    try {
      setLoading(true);
      const res = await authFetch('/api/history');
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setHistory(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const deleteEntry = async (id, e) => {
    e.stopPropagation();
    try {
      const res = await authFetch(`/api/history/${id}`, { method: 'DELETE' });
      if (!res.ok) { const d = await res.json(); throw new Error(d.error); }
      setHistory(prev => prev.filter(h => h.id !== id));
      if (expanded === id) setExpanded(null);
    } catch (err) { alert(err.message); }
  };

  const resetAll = async () => {
    setResetting(true);
    try {
      const res = await authFetch('/api/history', { method: 'DELETE' });
      if (!res.ok) { const d = await res.json(); throw new Error(d.error); }
      setHistory([]);
      setExpanded(null);
    } catch (err) { alert(err.message); }
    finally { setResetting(false); setConfirmReset(false); }
  };

  useEffect(() => { fetchHistory(); }, []);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Process History</h2>
          <p className="text-gray-500 text-sm mt-1">{history.length} records</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={fetchHistory} className="flex items-center gap-2 text-sm text-gray-600 hover:text-blue-600 px-3 py-2 rounded-lg hover:bg-blue-50">
            <RefreshCw className="w-4 h-4" />Refresh
          </button>
          {isAdmin && history.length > 0 && (
            <button onClick={() => setConfirmReset(true)}
              className="flex items-center gap-2 text-sm text-red-600 hover:text-red-700 px-3 py-2 rounded-lg hover:bg-red-50 border border-red-200">
              <Trash2 className="w-4 h-4" />Reset All
            </button>
          )}
        </div>
      </div>

      {/* Confirm Reset Modal */}
      {confirmReset && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6">
            <div className="w-12 h-12 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-3">
              <AlertTriangle className="w-6 h-6 text-red-600" />
            </div>
            <h3 className="font-bold text-gray-900 text-center mb-1">Reset All History?</h3>
            <p className="text-sm text-gray-500 text-center mb-5">
              This will permanently delete all {history.length} history entries. This cannot be undone.
            </p>
            <div className="flex gap-3">
              <button onClick={() => setConfirmReset(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-xl text-sm font-medium hover:bg-gray-50">
                Cancel
              </button>
              <button onClick={resetAll} disabled={resetting}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-xl text-sm font-semibold disabled:opacity-60">
                {resetting
                  ? <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                  : <Trash2 className="w-4 h-4" />}
                Delete All
              </button>
            </div>
          </div>
        </div>
      )}


      {loading ? (
        <div className="flex items-center justify-center h-48">
          <span className="w-6 h-6 border-2 border-blue-600/30 border-t-blue-600 rounded-full animate-spin" />
        </div>
      ) : history.length === 0 ? (
        <div className="text-center py-20 text-gray-400">
          <History className="w-12 h-12 mx-auto mb-4 opacity-30" />
          <p className="text-lg font-medium">No history yet</p>
          <p className="text-sm mt-1">Processed documents will appear here</p>
        </div>
      ) : (
        <div className="space-y-3">
          {history.map(item => {
            const sc = STATUS_CONFIG[item.status] || STATUS_CONFIG.processing;
            const isOpen = expanded === item.id;
            return (
              <div key={item.id} className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                <button
                  onClick={() => setExpanded(isOpen ? null : item.id)}
                  className="w-full flex items-center gap-4 px-5 py-4 hover:bg-gray-50 text-left"
                >
                  <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center shrink-0">
                    <FileText className="w-5 h-5 text-blue-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-gray-800 truncate text-sm">
                        {item.filename || 'Unnamed file'}
                      </span>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${sc.color}`}>
                        {sc.label}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 mt-1 text-xs text-gray-500 flex-wrap">
                      {item.subject && <span>Subject: {item.subject}</span>}
                      {item.year && <span>Year: {item.year}</span>}
                      {item.centre && <span>Centre: {item.centre}</span>}
                      {isAdmin && item.user_name && (
                        <span className="flex items-center gap-1">
                          <User className="w-3 h-3" />{item.user_name}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="text-right shrink-0 hidden sm:block">
                    <div className="text-xs text-gray-500">
                      {new Date(item.processed_at).toLocaleDateString()}
                    </div>
                    <div className="text-xs text-gray-400">
                      {new Date(item.processed_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </div>
                  </div>
                  {isAdmin && (
                    <button
                      onClick={e => deleteEntry(item.id, e)}
                      className="p-1.5 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded-lg shrink-0 transition-colors"
                      title="Delete this entry">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                  {isOpen ? <ChevronUp className="w-4 h-4 text-gray-400 shrink-0" /> : <ChevronDown className="w-4 h-4 text-gray-400 shrink-0" />}
                </button>

                {isOpen && (
                  <div className="border-t border-gray-100 px-5 py-4 bg-gray-50">
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                      <div className="text-center p-3 bg-white rounded-lg border border-gray-100">
                        <div className="text-2xl font-bold text-blue-600">{item.records_extracted || 0}</div>
                        <div className="text-xs text-gray-500 mt-1">Extracted</div>
                      </div>
                      <div className="text-center p-3 bg-white rounded-lg border border-gray-100">
                        <div className="text-2xl font-bold text-green-600">{item.records_approved || 0}</div>
                        <div className="text-xs text-gray-500 mt-1">Approved</div>
                      </div>
                      <div className="text-center p-3 bg-white rounded-lg border border-gray-100">
                        <div className="text-2xl font-bold text-purple-600">{item.records_saved || 0}</div>
                        <div className="text-xs text-gray-500 mt-1">Saved to DB</div>
                      </div>
                      <div className="text-center p-3 bg-white rounded-lg border border-gray-100">
                        <div className="text-2xl font-bold text-gray-600">{formatSize(item.file_size)}</div>
                        <div className="text-xs text-gray-500 mt-1">File Size</div>
                      </div>
                    </div>
                    {isAdmin && item.user_email && (
                      <div className="mt-3 text-xs text-gray-500">
                        Processed by: <strong>{item.user_name}</strong> ({item.user_email})
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
